import { useGetPerformProgressQuery } from '@/services/organizations';
import { Badge, BadgeVariant, Box, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ContractDocumentProviders,
  ContractRecipient,
  ContractStatus,
  isChangeOrderEligibleContract,
  PreBillingCustomerDiscountStatus,
  ProgramType,
  SalesRepCheckReason,
  UserPermissions,
} from 'types';

import {
  useCheckDisclosureStatusMutation,
  useGetContractSendProgressQuery,
  useLazyGetContractExportStatusQuery,
  useSendContractV2Mutation,
  useSyncContractMutation,
  useVoidContractMutation,
} from '../../services/contract';
import { getSalesRepCheckReasons } from '../../services/helpers';
import { useVoidQuoteMutation } from '../../services/quotes';
import { ContractListData } from '../../types/Contracts';
import { convertIsoToDateMed, convertIsoToTimeSimple } from '../Activity/InfiniteActivityList';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import RequirePermissions from '../auth/requirePermissions';

import { ContractSendReasonsModal } from './ContractSendReasonsModal';
import { VoidContractModal } from './VoidContractModal';

export function ContractListItem({
  item,
  preBillingCustomerDiscount,
  canCreateChangeOrder,
  programType,
  isInstallApproved,
}: {
  item: ContractListData;
  preBillingCustomerDiscount?: PreBillingCustomerDiscountStatus;
  canCreateChangeOrder?: boolean;
  programType: ProgramType;
  isInstallApproved?: boolean;
}): React.JSX.Element {
  const { contract, quote } = item;

  const [getExportStatus, { data: exportStatus, isFetching: isFetchingExportStatus }] =
    useLazyGetContractExportStatusQuery();

  const [sendContract, { isLoading: isSendingContract }] = useSendContractV2Mutation({
    fixedCacheKey: 'useSendContractV2MutationKey',
  });
  const [voidContract] = useVoidContractMutation({
    fixedCacheKey: 'useVoidContractMutationKey',
  });
  const [_voidQuote, { isLoading: isVoidingQuote }] = useVoidQuoteMutation({
    fixedCacheKey: 'useVoidQuoteMutationKey',
  });
  const allowsAction = useIsOrgReadOnly();
  const { data: isAccountEditEnabled } = useGetPerformProgressQuery(contract.accountId);
  useGetContractSendProgressQuery(
    { accountId: contract.accountId, contract },
    { skip: contract.status !== ContractStatus.sending },
  );

  const [checkDisclosureStatus, { isLoading: isCheckDisclosureLoading }] = useCheckDisclosureStatusMutation();
  const [syncContract, { isLoading: isSyncingContract }] = useSyncContractMutation();

  const illinoisShinesContract = contract.contractDocuments?.find(
    (cd) => cd.provider === ContractDocumentProviders.illinoisShines,
  );

  const contractHasExportableDocs = contract.contractDocuments?.some((cd) => cd.providerData?.shouldExport) ?? false;

  const navigate = useNavigate();
  const [isSubmitting, setSubmitting] = useState(false);
  const [sendReasons, setSendReasons] = useState<SalesRepCheckReason[] | undefined>();
  const closeSendReasons = useCallback(() => setSendReasons(undefined), []);
  const { isOpen: isVoidModalOpen, handleOpen: openVoidModal, handleClose: closeVoidModal } = useOpenClose();
  // Synchronous guard so rapid double/triple-clicks fire only one send mutation;
  // isSubmitting/isSendingContract only flip on the next render.
  const isSendInFlightRef = useRef(false);
  const contractStatuses: {
    [key in ContractStatus]: {
      label: string;
      variant: BadgeVariant;
    };
  } = {
    [ContractStatus.created]: {
      label: 'Created',
      variant: 'info',
    },
    [ContractStatus.sent]: {
      label: 'Sent',
      variant: 'info',
    },
    [ContractStatus.sending]: {
      label: 'Sending',
      variant: 'info',
    },
    [ContractStatus.disclosureSent]: {
      label: 'Pending Disclosure Signature',
      variant: 'info',
    },
    [ContractStatus.disclosureSigned]: {
      label: 'Disclosure Signed',
      variant: 'info',
    },
    [ContractStatus.partiallySigned]: {
      label: 'Partially Signed',
      variant: 'info',
    },
    [ContractStatus.signed]: {
      label: 'Signed',
      variant: 'info',
    },
    [ContractStatus.approved]: {
      label: 'Approved',
      variant: 'success',
    },
    [ContractStatus.voided]: {
      label: 'Voided',
      variant: 'warning',
    },
    [ContractStatus.rejected]: {
      label: 'Rejected',
      variant: 'danger',
    },
  };

  const willVoidPreBillingDiscountAmendment =
    preBillingCustomerDiscount === PreBillingCustomerDiscountStatus.accepted &&
    contract.status === ContractStatus.approved;

  const isChangeOrderEligibleOnVoid =
    isChangeOrderEligibleContract(programType, contract.status) && !isInstallApproved;

  // The actual void call — identical regardless of whether it was reached via the new modal's
  // "Confirm Void" or the plain window.confirm below.
  const handleConfirmVoid = async () => {
    setSubmitting(true);
    const toastId = toast.loading('Voiding contract');
    try {
      await voidContract({ accountId: contract.accountId, contractId: contract.id }).unwrap();
      toast.success('Contract voided successfully');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.data?.message || 'Error voiding contract');
    } finally {
      toast.dismiss(toastId);
      setSubmitting(false);
      closeVoidModal();
    }
  };

  const handleVoidContract = () => {
    if (isChangeOrderEligibleOnVoid) {
      openVoidModal();
      return;
    }

    const confirmMessage = willVoidPreBillingDiscountAmendment
      ? 'Are you sure you want to void this Contract? This will also void the pre-billing discount amendment. This action cannot be undone.'
      : 'Are you sure you want to void this Contract? This action cannot be undone.';
    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    void handleConfirmVoid();
  };

  const handleCreateChangeOrderFromModal = useCallback(() => {
    closeVoidModal();
    navigate(`/accounts/${contract.accountId}/change-order/create`);
  }, [closeVoidModal, navigate, contract.accountId]);

  const handleResendContract = async () => {
    if (isSendInFlightRef.current) return;
    isSendInFlightRef.current = true;
    setSubmitting(true);
    const toastId = toast.loading('Resending contract...');
    try {
      await sendContract({
        accountId: contract.accountId,
        contractId: contract.id,
        forceSend: true,
      }).unwrap();
    } catch (e: any) {
      const reasons = getSalesRepCheckReasons(e);
      if (reasons) {
        setSendReasons(reasons);
      } else if (e?.data?.errors) {
        toast.error(e?.data?.errors.join(', '));
      } else {
        toast.error(e?.data?.message || 'Error resending contract');
      }
    } finally {
      toast.dismiss(toastId);
      setSubmitting(false);
      isSendInFlightRef.current = false;
    }
  };

  const handleRefreshDisclosure = useCallback(async () => {
    await checkDisclosureStatus({
      accountId: contract.accountId,
      contractId: contract.id,
    });
  }, [checkDisclosureStatus, contract.accountId, contract.id]);

  const handleFetchExportStatus = useCallback(async () => {
    await getExportStatus({
      accountId: contract.accountId,
      contractId: contract.id,
    });
  }, [getExportStatus, contract.accountId, contract.id]);

  const handleSyncContract = useCallback(async () => {
    const toastId = toast.loading('Syncing contract');
    try {
      await syncContract({
        accountId: contract.accountId,
        contractId: contract.id,
      }).unwrap();
      toast.success('Contract synced successfully');
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error syncing contract');
    } finally {
      toast.dismiss(toastId);
    }
  }, [syncContract, contract.accountId, contract.id]);

  const handleSendContract = async () => {
    if (isSendInFlightRef.current) return;
    isSendInFlightRef.current = true;
    setSubmitting(true);
    const toastId = toast.loading('Sending contract');
    try {
      await sendContract({
        accountId: contract.accountId,
        contractId: contract.id,
        forceSend: false,
      }).unwrap();
    } catch (e: any) {
      const reasons = getSalesRepCheckReasons(e);
      if (reasons) {
        setSendReasons(reasons);
      } else if (e?.data?.errors) {
        toast.error(e?.data?.errors.join(', '));
      } else {
        toast.error(e?.data?.message || 'Error sending contract');
      }
    } finally {
      toast.dismiss(toastId);
      setSubmitting(false);
      isSendInFlightRef.current = false;
    }
  };

  const contractStatus = contract.status;
  const docusignContract = contract.contractDocuments?.find(
    (cd) => cd.provider === ContractDocumentProviders.docusign,
  );

  return (
    <Box borderWidth="xs 0 0 0" borderColor="separator" padding="md lg" direction="row" childGap="lg">
      <Box width="xl">
        <Badge
          style={{ width: 'fit-content' }}
          message={contractStatuses[contractStatus as keyof typeof ContractStatus]?.label}
          variant={contractStatuses[contractStatus as keyof typeof ContractStatus]?.variant}
        />
      </Box>
      <Box flex="auto" childGap="xs">
        <Box>{quote?.productName || '---'}</Box>
        {contractStatus !== ContractStatus.voided &&
          docusignContract?.recipients &&
          docusignContract.recipients.map((recipient: ContractRecipient, index: number) => (
            <Box color="body-secondary" key={`${index}-${recipient.name}`}>
              {recipient.name} -{' '}
              {recipient.status === 'created'
                ? 'signature required'
                : recipient.status === 'sent'
                  ? `sent to ${recipient.email}`
                  : `signed ${
                      recipient.signedAt
                        ? `${convertIsoToDateMed(recipient.signedAt)} ${convertIsoToTimeSimple(recipient.signedAt)}`
                        : '--'
                    }`}
            </Box>
          ))}
        {exportStatus && (
          <Box flex="auto" direction="row" alignItems="center" gap="xs" padding="xs 0 xs 0">
            <Box>Vault Status:</Box>
            <Badge
              style={{ width: 'fit-content' }}
              message={exportStatus.overallStatus}
              variant="info"
              title={exportStatus.envelopes?.[0]?.message}
            />
          </Box>
        )}
        {illinoisShinesContract && (
          <Box borderWidth="xs 0 0 0" borderColor="separator" padding="xs 0 xs 0">
            <Box direction="row" alignItems="center" padding="0 0 xs 0">
              <Box margin="0 md 0 0">Illinois Shines Disclosure Document</Box>
              {![ContractStatus.voided, ContractStatus.created].includes(contract.status) && (
                <Badge
                  style={{ width: 'fit-content' }}
                  message={illinoisShinesContract.status.toUpperCase()}
                  variant="info"
                />
              )}
            </Box>
            {illinoisShinesContract?.recipients &&
              illinoisShinesContract.recipients.map((recipient: ContractRecipient, index: number) => (
                <Box color="body-secondary" key={`${index}-${recipient.name}`}>
                  {recipient.name} -{' '}
                  {recipient.status === 'created'
                    ? 'signature required'
                    : recipient.status === 'sent'
                      ? `sent to ${recipient.email}`
                      : `signed ${
                          recipient.signedAt
                            ? `${convertIsoToDateMed(recipient.signedAt)} ${convertIsoToTimeSimple(recipient.signedAt)}`
                            : '--'
                        }`}
                </Box>
              ))}
          </Box>
        )}
      </Box>

      <Box direction="column" justifyContent="space-between">
        <Box direction="row" alignSelf="flex-end" childGap="xs">
          {(contractStatus === ContractStatus.created || contractStatus === ContractStatus.disclosureSigned) && (
            <Box>
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.editor]}
                checkAllPermissions={false}
              >
                <Button
                  size="sm"
                  variant="primary"
                  isLoading={isSubmitting || isSendingContract}
                  isDisabled={
                    isSubmitting || isSendingContract || isVoidingQuote || !isAccountEditEnabled || !allowsAction
                  }
                  onClick={handleSendContract}
                >
                  Send
                </Button>
              </RequirePermissions>
            </Box>
          )}
          {[ContractStatus.sent, ContractStatus.partiallySigned].includes(contractStatus) && (
            <Box>
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.editor, UserPermissions.lightReachSupport]}
                checkAllPermissions={false}
              >
                <Button
                  size="sm"
                  variant="primary"
                  isLoading={isSubmitting || isSendingContract}
                  isDisabled={
                    isSubmitting || isSendingContract || isVoidingQuote || !isAccountEditEnabled || !allowsAction
                  }
                  onClick={handleResendContract}
                >
                  Resend
                </Button>
              </RequirePermissions>
            </Box>
          )}
          {!!docusignContract &&
            [ContractStatus.sent, ContractStatus.partiallySigned, ContractStatus.signed].includes(
              contractStatus,
            ) && (
              <Box>
                <RequirePermissions permissions={[UserPermissions.lightReachSupport]} checkAllPermissions={false}>
                  <Button
                    size="sm"
                    variant="secondary"
                    isDisabled={
                      isSubmitting ||
                      isSendingContract ||
                      isVoidingQuote ||
                      isSyncingContract ||
                      !isAccountEditEnabled ||
                      !allowsAction
                    }
                    isLoading={isSyncingContract}
                    onClick={handleSyncContract}
                  >
                    Check Status
                  </Button>
                </RequirePermissions>
              </Box>
            )}
          {contractStatus === ContractStatus.approved && contractHasExportableDocs && (
            <Box>
              <RequirePermissions
                permissions={[
                  UserPermissions.lightReachAccountingAdmin,
                  UserPermissions.lightReachSupport,
                  UserPermissions.lightReachAccountingSupport,
                ]}
                checkAllPermissions={false}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  tone="neutral"
                  isDisabled={
                    isSubmitting ||
                    isCheckDisclosureLoading ||
                    isSendingContract ||
                    isVoidingQuote ||
                    !isAccountEditEnabled ||
                    !allowsAction
                  }
                  isLoading={isFetchingExportStatus}
                  onClick={handleFetchExportStatus}
                >
                  Check Vault Status
                </Button>
              </RequirePermissions>
            </Box>
          )}
          {canCreateChangeOrder && isChangeOrderEligibleContract(programType, contractStatus) && (
            <Box style={{ whiteSpace: 'nowrap' }}>
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.editor]}
                checkAllPermissions={false}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  tone="neutral"
                  isDisabled={
                    isSubmitting || isSendingContract || isVoidingQuote || !isAccountEditEnabled || !allowsAction
                  }
                  onClick={() => navigate(`/accounts/${contract.accountId}/change-order/create`)}
                >
                  Change Order
                </Button>
              </RequirePermissions>
            </Box>
          )}
          {![ContractStatus.created, ContractStatus.voided].includes(contractStatus) && (
            <Box>
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.editor]}
                checkAllPermissions={false}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  tone="danger"
                  isDisabled={
                    isSubmitting ||
                    isCheckDisclosureLoading ||
                    isSendingContract ||
                    isVoidingQuote ||
                    !isAccountEditEnabled ||
                    !allowsAction
                  }
                  onClick={handleVoidContract}
                >
                  Void
                </Button>
              </RequirePermissions>
            </Box>
          )}
        </Box>
        <Box direction="row" alignSelf="flex-end">
          {ContractStatus.disclosureSent === contractStatus && (
            <Box>
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.editor]}
                checkAllPermissions={false}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={isCheckDisclosureLoading}
                  onClick={handleRefreshDisclosure}
                  isDisabled={
                    !allowsAction || isSubmitting || isCheckDisclosureLoading || isSendingContract || isVoidingQuote
                  }
                >
                  Check Status
                </Button>
              </RequirePermissions>
            </Box>
          )}
        </Box>
      </Box>
      {sendReasons && <ContractSendReasonsModal reasons={sendReasons} onClose={closeSendReasons} />}
      {isChangeOrderEligibleOnVoid && (
        <VoidContractModal
          isOpen={isVoidModalOpen}
          isVoiding={isSubmitting}
          canCreateChangeOrder={canCreateChangeOrder}
          willVoidPreBillingDiscountAmendment={willVoidPreBillingDiscountAmendment}
          onDismiss={closeVoidModal}
          onCreateChangeOrder={handleCreateChangeOrderFromModal}
          onConfirmVoid={handleConfirmVoid}
        />
      )}
    </Box>
  );
}
